import {Component, OnInit} from '@angular/core';
import {Messages, Platform, Product, ProductOffering, ProductService, UserService} from '@uofu-uss-aux/software-catalog-ng-core';
import {catchError, first, map} from 'rxjs/operators';
import {
  FilterChoice,
  ProductFilterChooserComponent
} from '../../product/products/product-filter/product-filter-chooser/product-filter-chooser.component';
import {ProductFilterService} from '../../product/products/product-filter/product-filter.service';
import {combineLatest, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {UserAccessService} from '../../user/user-access.service';
import {groupBy, mapValues, sortBy, toPairs, uniqBy} from 'lodash-es';
import {NotificationService} from '@uofu-uss/angular-util';
import {DownloadWithoutPurchaseEntryComponent} from '../download-without-purchase-entry/download-without-purchase-entry.component';
import {AsyncPipe, NgClass, NgForOf, NgIf} from '@angular/common';
import {ChunkPipe} from '../chunk-pipe/chunk.pipe';

interface DownloadFilesEntry {
  product: Product;
  version: string;
  platforms: readonly Platform[];
}

/* A product tupled with its downloadable offerings
 * This is only used intermediately */
interface ProductAndItsDownloadableOfferings {
  product: Product;
  offerings: readonly ProductOffering[];
}

@Component({
  selector: 'app-download-without-purchase-index',
  templateUrl: './download-without-purchase-index.component.html',
  styleUrls: ['./download-without-purchase-index.component.scss'],
  standalone: true,
  imports: [
    ProductFilterChooserComponent,
    DownloadWithoutPurchaseEntryComponent,
    NgIf,
    NgForOf,
    NgClass,
    AsyncPipe,
    ChunkPipe,
  ]
})
export class DownloadWithoutPurchaseIndexComponent implements OnInit {
  constructor(private readonly productService: ProductService,
              private readonly userService: UserService,
              private readonly userAccessService: UserAccessService,
              private readonly notificationService: NotificationService) {
  }

  entries$: Observable<readonly DownloadFilesEntry[]>;

  // The product filter initializes with 'See All'.
  // ReplaySubject will save that for the first time that products are loaded.
  filter$: Subject<FilterChoice> = new ReplaySubject<FilterChoice>(1);

  /**
   * For every product in products,
   *     find all the product's offerings which pass the filter
   *
   *     Since offerings do not contain a reference to their product,
   *     the product and its passing offerings are placed in a tuple to
   *     maintain association between the product and their offerings.
   *
   * @param products the products retrieved from the fetchProducts
   * @param filter the FilterChoice (emitted from the ProductFilterChooser)
   */
  static findAvailableOfferingsMatchingFilters([products, filter]): readonly ProductAndItsDownloadableOfferings[] {
    return products.map(product => ({
      product,
      offerings: product.licenses
        // Filter for licenses that match the selection
        .filter(license => ProductFilterService.licenseTypeMatches(license.type, filter.licenseTypeChoice))
        // Retrieve all the offerings that pass the license-filter
        .flatMap(license => license.offerings)
        // Filter for offerings that have the platform that matches the platform-selection
        .filter(offering => ProductFilterService.platformMatches(offering.platform.title, filter.platformChoice))
    }));
  }

  /**
   * Takes a product and its offerings and builds a DownloadFileEntry
   *
   * ---
   * Offerings with matching versions are first grouped together
   *
   * For every offering of a particular version,
   *     Get the all platforms of the offering
   *
   * Build an entry for each version,
   *     the version,
   *     the product which the version belongs to,
   *     the platforms available for the version
   */
  static buildEntriesFromProductAndOfferings(
    {
      product,
      offerings
    }: ProductAndItsDownloadableOfferings): DownloadFilesEntry[] {
    // Group offerings with the same version
    // ... => {'2.0': [{id: 1...}, {id: 2...}], '3.0': [{id: 3...}]}
    const offeringsGroupedByVersion = groupBy(offerings, 'version');

    // Get the platforms available for a version
    // ... => {'2.0': [WINDOWS, MAC, LINUX], '3.0': [WINDOWS]}
    const platformsGroupedByVersion =
      mapValues(offeringsGroupedByVersion, _offerings =>
        sortBy(uniqBy(_offerings.flatMap(o => o.platform), 'id'), 'id'));

    // Get the key-value pairs [version, platforms]
    // ... => [['2.0', [WINDOWS, MAC, LINUX]], ['3.0, [WINDOWS]]]
    const versionToPlatformPairs = toPairs(platformsGroupedByVersion);

    return versionToPlatformPairs.flatMap(([version, platforms]) => ({
      product,
      version,
      platforms
    } as DownloadFilesEntry));
  }

  ngOnInit(): void {
    const products$: Observable<Product[]> = this.productService.fetchProducts(
      {
        licenseTypes: this.userAccessService.determineAllowedLicenseTypes(this.userService.value),
        downloadWithoutPurchase: true,
      })
      .pipe(
        first(),
        catchError(error => {
          this.notificationService.addError(Messages.validationMsg(error, 'Unable to fetch product list'));
          return of([]);
        }),
      );

    this.entries$ =
      combineLatest([products$, this.filter$])
        .pipe(
          map(DownloadWithoutPurchaseIndexComponent.findAvailableOfferingsMatchingFilters),
          map(productsAndTheirDownloadableOfferings => productsAndTheirDownloadableOfferings
            .flatMap(DownloadWithoutPurchaseIndexComponent.buildEntriesFromProductAndOfferings)),
          map(entries => sortBy(entries, ['product.title', 'version'])),
        );
  }

  updateFilter($event: FilterChoice) {
    this.filter$.next($event);
  }

  hasFilterChosen(filterChoice?: FilterChoice) {
    return ProductFilterService.hasFilterChosen(filterChoice);
  }
}
